import classnames from 'classnames';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayingStatusIcon } from 'components/rivals/PlayingStatusIcon';
import { DraftPositionType } from 'components/rivals/draft/lib';

import { PickerSlotContent } from './PickerSlotContent';

const Remove = styled(ButtonBase)`
  position: absolute;
  right: calc(var(--half-unit) * -1);
  top: calc(var(--half-unit) * -1);
  align-items: center;
  justify-content: center;
  margin: 0;
  background: var(--c-black);
  color: var(--c-nd-600);
  border-radius: 50%;
  width: var(--double-and-a-half-unit);
  height: 20px;
  cursor: pointer;
`;
const CardContainer = styled.div`
  position: relative;
  ${Remove} {
    display: inline-flex;
  }
  @media ${desktopAndAbove} {
    ${Remove} {
      display: none;
    }
    &:hover,
    &:focus {
      ${Remove} {
        display: inline-flex;
      }
    }
  }
`;
const Card = styled(ButtonBase)`
  display: inline-block;
  width: 60px;
  height: 60px;
  overflow: hidden;
  isolation: isolate;
  aspect-ratio: 1;
  border: 1px solid var(--c-nd-400);
  border-radius: 16px;
  color: var(--c-nd-400);
  cursor: pointer;
  position: relative;
  &.withPicture {
    border: 2px solid var(--c-black);
  }
  &.active,
  &.error {
    color: var(--c-white);
    border: 2px solid var(--c-brand-600);
    background-color: rgba(var(--c-rgb-brand-600), 0.4);
    &.withPicture::before {
      position: absolute;
      inset: -2px;
      content: '';
      background-color: rgba(var(--c-rgb-brand-600), 0.4);
      z-index: 1;
      border-radius: inherit;
    }
  }
  &:hover,
  &:focus {
    border-color: var(--c-brand-600);
  }

  &.error {
    color: var(--c-static-red-300);
    border: 2px solid var(--c-static-red-300);
    background-color: rgba(var(--c-rgb-red-300), 0.4);
    &.withPicture::before {
      background-color: rgba(var(--c-rgb-red-300), 0.4);
    }
  }
`;
const CostWrapper = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 50%);
`;
const StartingStatusWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  left: calc(-1 * var(--half-unit));
`;

type PickerCardProps = {
  position: DraftPositionType;
  active?: boolean;
  error?: boolean;
  onClick?: () => void;
  drafted?: Nullable<{
    value: number;
    avatarUrl?: string;
    player: { displayName: string };
  }>;
  remove?: () => void;
  starting?: boolean;
  displayStartingStatus?: boolean;
};

export const PickerCard = ({
  active,
  error,
  onClick,
  drafted,
  position,
  remove,
  starting = false,
  displayStartingStatus = false,
}: PickerCardProps) => {
  return (
    <CardContainer>
      <Card
        className={classnames({
          active,
          error,
          withPicture: drafted,
        })}
        onClick={onClick}
        type="button"
      >
        <PickerSlotContent draftablePlayer={drafted} position={position} />
      </Card>
      {!!drafted && (
        <>
          {!!remove && (
            <Remove onClick={remove}>
              <svg width="12" height="12" viewBox="0 0 12 12">
                <path
                  d="M3.87879 5.69787L0.439453 2.25853L2.56077 0.137207L6.00011 3.57655L9.43945 0.137207L11.5608 2.25853L8.12143 5.69787L11.5608 9.13721L9.43945 11.2585L6.00011 7.81919L2.56077 11.2585L0.439453 9.13721L3.87879 5.69787Z"
                  fill="currentcolor"
                />
              </svg>
            </Remove>
          )}
          <CostWrapper>
            <Cost invalid={error} score={drafted?.value || 0} />
          </CostWrapper>
        </>
      )}
      {displayStartingStatus && drafted && (
        <StartingStatusWrapper>
          <PlayingStatusIcon isStarting={starting} />
        </StartingStatusWrapper>
      )}
    </CardContainer>
  );
};
